import { useCallback, useEffect, useState } from 'react';
import { onboardingService } from '../../services/onboardingService';
import { normalizeOnboardingActionPath } from '../../constants/onboardingStepNavigation';
import type { OnboardingStatusResponse, OnboardingStepItem } from '../../types/onboarding';

function normalizeOnboardingStep(step: OnboardingStepItem): OnboardingStepItem {
  return normalizeOnboardingActionPath(step);
}

function normalizeOnboardingResponse(res: OnboardingStatusResponse): OnboardingStatusResponse {
  return {
    ...res,
    steps: res.steps.map(normalizeOnboardingStep),
  };
}

export const useOnboardingStatus = () => {
  const [data, setData] = useState<OnboardingStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await onboardingService.getStatus();
      setData(normalizeOnboardingResponse(res));
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const dismissStep = useCallback(
    async (stepId: string) => {
      await onboardingService.dismissOptionalStep(stepId);
      await fetchStatus();
    },
    [fetchStatus]
  );

  return {
    data,
    isLoading,
    error,
    refetch: fetchStatus,
    dismissStep,
    tourDismissed: data?.tourDismissed ?? false,
    tourCurrentStepId: data?.tourCurrentStepId ?? null,
  };
};
